import * as React from 'react';
import { useMemo } from 'react';
import {
  Box,
  Card,
  Chip,
  IconButton,
  lighten,
  Stack,
  Typography,
} from '@mui/material';
import {
  Eye as EyeIcon,
  UsersThree as UsersThreeIcon,
} from '@phosphor-icons/react';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';

import type { User } from '@/types/v2/user';
import { paths } from '@/paths';
import { UserTableTile } from '@/components/core/v2/user-table-tile';

interface SystemUsersTableProps {
  rows: User[];
  loading: boolean;
}

export function SystemUsersTable({ rows, loading }: SystemUsersTableProps): React.JSX.Element {
  const navigate = useNavigate();
  
  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
        accessorFn: (row) => {
          return <UserTableTile user={row} />;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 200,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        size: 150,
      },
      {
        id: 'communities',
        header: 'Communities',
        size: 120,
        accessorFn: (row) => {
          const communityCount = row.communities?.length || 0;
          return communityCount > 0 ? (
            <Chip 
              label={communityCount} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          ) : (
            <Typography color="text.secondary" variant="body2">None</Typography>
          );
        },
      },
      {
        id: 'units',
        header: 'Units',
        size: 120,
        accessorFn: (row) => {
          const unitCount = row.units?.length || 0;
          return unitCount > 0 ? (
            <Chip 
              label={unitCount} 
              size="small" 
              color="secondary" 
              variant="outlined"
            />
          ) : (
            <Typography color="text.secondary" variant="body2">None</Typography>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        size: 120,
        accessorFn: (row) => {
          if (row.email_verified_at) {
            return <Chip label="Verified" size="small" color="success" />;
          }
          return <Chip label="Pending" size="small" color="warning" />;
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable<User>({
    columns,
    data: rows,
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    state: {
      isLoading: loading,
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <IconButton
        onClick={() => {
          navigate(paths.settings.account.systemUsers.user(row.original.id));
        }}
        size="small"
      >
        <EyeIcon size={20} />
      </IconButton>
    ),
    renderTopToolbar: ({ table }) => {
      return (
        <Box
          sx={(theme) => ({
            backgroundColor: lighten(theme.palette.background.default, 0.05),
            display: 'flex',
            gap: '0.5rem',
            p: '8px',
            justifyContent: 'space-between',
          })}
        >
          <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <MRT_GlobalFilterTextField table={table} />
            <MRT_ToggleFiltersButton table={table} />
          </Box>
        </Box>
      );
    },
  });

  return (
    <Card>
      <Box 
        sx={{ 
          p: { xs: 2, md: 3 }, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <UsersThreeIcon size={24} style={{ marginRight: '8px', color: 'var(--mui-palette-primary-main)' }} />
            <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle1">
              {rows.length} {rows.length === 1 ? 'User' : 'Users'}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <MaterialReactTable table={table} />
    </Card>
  );
}
